/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { format } from "date-fns";
import React from "react";
import { Redirect } from "react-router-dom";
import { isFeatureEnabled } from "utility/isFeatureEnabled";

import { getEventDashboardPermissionErrorResolution } from "../eventDashboardPermissionError";
import { AdminSalesDashboardHeader } from "./AdminSalesDashboardHeader";
import { AdminSalesDashboardSummary } from "./AdminSalesDashboardSummary";
import { DashboardSectionTitle } from "./DashboardSectionTitle";
import { EventSalesDetailsTable } from "./EventSalesDetailsTable";
import { PurchaserQuestionnaireDashboard } from "./PurchaserQuestionnaireDashboard";
import { SalesDashboardPermissionError } from "./SalesDashboardPermissionError";
import { TicketSalesByOrderDateCumulativeDashboard } from "./TicketSalesByOrderDateCumulativeDashboard";
import { TicketSalesByOrderDateDashboard } from "./TicketSalesByOrderDateDashboard";
import { UserAttributeDashboard } from "./UserAttributeDashboard";
import { minBigInt } from "./minBigInt";
import { useAdminEventParams } from "./useAdminEventParams";
import { useGetEventSalesDetails } from "./useGetEventSalesDetails";
import { useGetSalesDashboardEvents } from "./useGetSalesDashboardEvents";
import { useGetTicketQuestionAnswers } from "./useGetTicketQuestionAnswers";
import { useGetTicketSalesByOrderDate } from "./useGetTicketSalesByOrderDate";
import { useGetUserAttributeCounts } from "./useGetUserAttributeCounts";

const formatBigqueryLastWrittenAt = (
  bigqueryLastWrittenAt: bigint | undefined
): string => {
  if (bigqueryLastWrittenAt === undefined) {
    return "未取得";
  }

  return format(new Date(Number(bigqueryLastWrittenAt)), "yyyy/M/d H:mm:ss");
};

const formatOrderDate = (orderDate: bigint): string =>
  new Intl.DateTimeFormat("ja-JP", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(new Date(Number(orderDate)));

export const AdminSalesDashboard: React.FC = () => {
  const { eventIds } = useAdminEventParams();
  const isUserInfoEnabled = isFeatureEnabled({ featureName: "userInfo" });
  const isUserAnswerEnabled = isFeatureEnabled({
    featureName: "showUserAnswer",
  });
  const { events, eventNames } = useGetSalesDashboardEvents(eventIds);
  const {
    error: eventSalesDetailsError,
    fetchedData: eventSalesDetailsResponse,
    isLoading: isLoadingEventSales,
    ticketSalesDetails,
    otherSalesDetails,
  } = useGetEventSalesDetails(eventIds);
  const {
    error: ticketSalesError,
    fetchedData: ticketSalesResponse,
    isLoading: isLoadingTicketSales,
  } = useGetTicketSalesByOrderDate(eventIds);
  const {
    error: userAttributeCountsError,
    fetchedData: userAttributeCountsResponse,
    isLoading: isLoadingUserAttributeCounts,
  } = useGetUserAttributeCounts(isUserInfoEnabled ? eventIds : []);
  const {
    error: ticketQuestionAnswersError,
    fetchedData: ticketQuestionAnswersResponse,
    isLoading: isLoadingTicketQuestionAnswers,
  } = useGetTicketQuestionAnswers(isUserAnswerEnabled ? eventIds : []);

  const permissionErrorResolution = getEventDashboardPermissionErrorResolution([
    eventSalesDetailsError,
    ticketSalesError,
    userAttributeCountsError,
    ticketQuestionAnswersError,
  ]);

  const isSummaryLoading = isLoadingEventSales && !eventSalesDetailsResponse;

  const fetchedAt = formatBigqueryLastWrittenAt(
    minBigInt([
      eventSalesDetailsResponse?.bigqueryLastWrittenAt,
      ticketSalesResponse?.bigqueryLastWrittenAt,
      // TODO: 多分必要？
      // userAttributeCountsResponse?.bigqueryLastWrittenAt,
      ticketQuestionAnswersResponse?.bigqueryLastWrittenAt,
    ])
  );

  const orderDateRange = React.useMemo(() => {
    const ticketSalesByOrderDate =
      ticketSalesResponse?.ticketSalesByOrderDate ?? [];
    const [firstTicketSale] = ticketSalesByOrderDate;

    if (!firstTicketSale) {
      return undefined;
    }

    return ticketSalesByOrderDate.reduce(
      ({ oldestOrderDate, newestOrderDate }, { orderDate }) => ({
        oldestOrderDate:
          orderDate < oldestOrderDate ? orderDate : oldestOrderDate,
        newestOrderDate:
          orderDate > newestOrderDate ? orderDate : newestOrderDate,
      }),
      {
        oldestOrderDate: firstTicketSale.orderDate,
        newestOrderDate: firstTicketSale.orderDate,
      }
    );
  }, [ticketSalesResponse]);

  const ticketSummaryCards = React.useMemo(() => {
    const ticketSalesSummary = ticketSalesDetails.reduce<{
      confirmedSales: number;
      salesCount: number;
    }>(
      (summary, detail) => {
        return {
          confirmedSales:
            summary.confirmedSales + detail.price * detail.quantity,
          salesCount: summary.salesCount + detail.quantity,
        };
      },
      {
        confirmedSales: 0,
        salesCount: 0,
      }
    );

    return [
      {
        label: "確定売上",
        prefix: "¥",
        value: ticketSalesSummary.confirmedSales.toLocaleString("ja-JP"),
      },
      {
        label: "販売数",
        value: ticketSalesSummary.salesCount.toLocaleString("ja-JP"),
        unit: "枚",
      },
    ];
  }, [ticketSalesDetails]);

  if (permissionErrorResolution === "redirect") {
    return <Redirect to="/" />;
  }

  if (permissionErrorResolution === "alternative-event-authorized") {
    return <SalesDashboardPermissionError />;
  }

  return (
    <main css={styles.page}>
      <div css={styles.inner}>
        <AdminSalesDashboardHeader
          eventNames={eventNames}
          fetchedAt={fetchedAt}
          eventIds={eventIds}
        />

        <div css={styles.contents}>
          <section css={styles.section}>
            <DashboardSectionTitle title="サマリー" />
            <AdminSalesDashboardSummary
              error={eventSalesDetailsError}
              isSummaryLoading={isSummaryLoading}
              summaryCards={
                eventSalesDetailsResponse ? ticketSummaryCards : undefined
              }
            />
          </section>

          <section css={styles.section}>
            <DashboardSectionTitle
              title="券種別"
              subText={`${ticketSalesDetails.length}券種`}
            />

            <EventSalesDetailsTable
              error={eventSalesDetailsError}
              isLoading={isLoadingEventSales}
              salesDetails={
                eventSalesDetailsResponse ? ticketSalesDetails : undefined
              }
            />
          </section>

          <section css={styles.section}>
            <DashboardSectionTitle
              title="商品別"
              subText={`${otherSalesDetails.length}種類`}
            />

            <EventSalesDetailsTable
              columnLabel={"商品"}
              error={eventSalesDetailsError}
              isLoading={isLoadingEventSales}
              salesDetails={
                eventSalesDetailsResponse ? otherSalesDetails : undefined
              }
            />
          </section>

          <section css={styles.section}>
            <DashboardSectionTitle
              title="販売推移"
              subText={
                orderDateRange &&
                `支払日 ${formatOrderDate(
                  orderDateRange.oldestOrderDate
                )} - ${formatOrderDate(orderDateRange.newestOrderDate)}`
              }
            />
            <TicketSalesByOrderDateCumulativeDashboard
              data={ticketSalesResponse}
              error={ticketSalesError}
              isLoading={isLoadingTicketSales}
            />
            <TicketSalesByOrderDateDashboard
              data={ticketSalesResponse}
              error={ticketSalesError}
              isLoading={isLoadingTicketSales}
            />
          </section>

          {isUserInfoEnabled && (
            <section css={styles.section}>
              <DashboardSectionTitle
                title="購入者属性"
                subText={
                  userAttributeCountsResponse &&
                  userAttributeCountsResponse.deletedPurchaserCount > 0
                    ? `※退会したユーザー ${userAttributeCountsResponse.deletedPurchaserCount}人を含みます（退会したユーザーは「未回答」に分類されます）`
                    : undefined
                }
              />

              <UserAttributeDashboard
                data={userAttributeCountsResponse}
                error={userAttributeCountsError}
                isLoading={isLoadingUserAttributeCounts}
              />
            </section>
          )}

          {isUserAnswerEnabled && (
            <section css={styles.section}>
              <div css={styles.questionnaireSectionHeading}>
                <DashboardSectionTitle title="購入者アンケート（回答結果）" />
                <p css={styles.questionnaireSectionDescription}>
                  主催者が設定した2択アンケートの回答結果。券種別の「はい」回答率もあわせて表示。
                </p>
              </div>
              <PurchaserQuestionnaireDashboard
                data={ticketQuestionAnswersResponse}
                error={ticketQuestionAnswersError}
                events={events}
                isLoading={isLoadingTicketQuestionAnswers}
              />
            </section>
          )}
        </div>
      </div>
    </main>
  );
};

const styles = {
  page: css`
    width: 100%;
    min-height: 100vh;
    padding: clamp(20px, 3.5vw, 30px) clamp(16px, 4vw, 44px)
      clamp(48px, 7vw, 72px);
    background: #131c26;
  `,
  inner: css`
    display: flex;
    flex-direction: column;
    gap: clamp(18px, 3vw, 26px);
    width: 100%;
    max-width: 1360px;
    margin: 0 auto;
  `,
  contents: css`
    display: flex;
    flex-direction: column;
    gap: clamp(28px, 4vw, 40px);
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: 14px;
  `,
  questionnaireSectionHeading: css`
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
  questionnaireSectionDescription: css`
    margin: 0;
    color: #66737f;
    font-size: 12.5px;
    letter-spacing: 0.3px;
    line-height: 1.5;
  `,
};
